import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Button } from '../ui/button';
import { ClipboardList, AlertTriangle } from 'lucide-react';

/**
 * SurveyPayoutBanner
 *
 * Iter-133u (Phase D) — shown on the Talent Dashboard when one or more
 * completed bookings have a payout blocked because the post-event
 * survey has not been submitted. Tapping "Complete Survey" deep-links
 * into the per-booking survey form.
 *
 * Backed by GET /api/talent/payouts/survey-gated which already filters
 * out:
 *   • surveys already submitted
 *   • bookings with an admin override applied
 *   • bookings with a payout already in-flight or paid
 *   • roles that do not require a survey
 */
const SurveyPayoutBanner = () => {
  const navigate = useNavigate();
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    fetch(`${baseUrl}/api/talent/payouts/survey-gated`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.json() : { blocked_bookings: [] })
      .then((d) => setBlocked(d?.blocked_bookings || []))
      .catch(() => setBlocked([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading || blocked.length === 0) return null;

  const first = blocked[0];
  const more = blocked.length - 1;

  return (
    <Alert
      className="mb-6 bg-rose-50 border-rose-300"
      data-testid="survey-payout-banner"
    >
      <AlertTriangle className="h-4 w-4 text-rose-600" />
      <AlertTitle className="text-rose-800 font-semibold">
        Post-event survey required before payout
      </AlertTitle>
      <AlertDescription className="text-rose-700 mt-1">
        <div className="mb-2 text-sm">
          Your payout for <strong>{first.role_name}</strong>
          {first.venue_name ? <> @ <strong>{first.venue_name}</strong></> : null}
          {' '}is on hold until you submit the post-event survey.
          {more > 0 && (
            <span className="block text-xs text-rose-600 mt-1">
              +{more} more booking{more > 1 ? 's' : ''} waiting on a survey.
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            className="bg-rose-600 hover:bg-rose-700 text-white"
            onClick={() => navigate(first.survey_url)}
            data-testid="complete-survey-btn"
          >
            <ClipboardList className="w-4 h-4 mr-2" />
            Complete Survey
          </Button>
          {blocked.slice(1, 3).map((b) => (
            <Button
              key={b.booking_id}
              size="sm"
              variant="outline"
              className="border-rose-300 text-rose-700 hover:bg-rose-100"
              onClick={() => navigate(b.survey_url)}
              data-testid={`complete-survey-btn-${b.booking_id}`}
            >
              {b.role_name} @ {b.venue_name || 'event'}
            </Button>
          ))}
        </div>
      </AlertDescription>
    </Alert>
  );
};

export default SurveyPayoutBanner;
